import { type ReactElement, useEffect, useRef } from "react"
import styled from "styled-components"
import type { DetailSession } from "~/features/detail/types"
import { RemoteImage } from "~/shared/ui"
import { PlayCircleIcon } from "~/shared/ui/icons"

/** Figma's session artwork is 545 × 320. */
const THUMBNAIL_RATIO = 545 / 320

const Root = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg};
  min-width: 0;
`

/**
 * The heading from node 400-2757. It lives here rather than on the control that
 * opens the panel, because that control is now an icon — leaving the list with
 * no visible name at all.
 */
const Heading = styled.h2`
  padding-inline: ${({ theme }) => theme.player.edgeInline};
  font-size: ${({ theme }) => theme.fontSize.xl};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  line-height: ${({ theme }) => theme.lineHeight.tight};
  color: ${({ theme }) => theme.player.textPrimary};
`

/**
 * The scrollbar is deliberately visible.
 *
 * It was hidden to match the OTT frame, but that frame is a TV screen where the
 * remote does the scrolling and nothing needs to advertise it. On a desktop with
 * a mouse there is otherwise no indication the rail moves at all — a slim
 * translucent bar is the cheapest possible affordance, and it doubles as a
 * position indicator for a long study.
 *
 * Snapping is `proximity`, not `mandatory`: mandatory snapping on items this wide
 * fights a trackpad, and the rail is for browsing rather than paging.
 */
const Rail = styled.ul.attrs({ role: "list" })`
  display: flex;
  gap: ${({ theme }) => theme.spacing.xl};
  margin: 0;

  /*
   * Full-bleed: the scroll container spans the whole width and creates its own
   * safe area with padding, rather than being inset by an ancestor. That is what
   * lets a card travel to the screen edge instead of vanishing into an invisible
   * box, while the first and last still line up with the controls above.
   *
   * The block padding is what keeps the selection and focus rings off the
   * clipping edge — overflow-x makes overflow-y a clipping box too.
   *
   * Doubled selector for specificity: global.css resets ul[role="list"] to
   * padding: 0, and an attribute-plus-type selector outranks a single generated
   * class, so without this the safe area silently does not apply.
   */
  && {
    padding-block: ${({ theme }) => theme.spacing.sm};
    padding-inline: ${({ theme }) => theme.player.edgeInline};
  }

  scroll-padding-inline: ${({ theme }) => theme.player.edgeInline};

  overflow-x: auto;
  scroll-snap-type: x proximity;
  overscroll-behavior-x: contain;
  scrollbar-width: thin;
  scrollbar-color: rgb(255 255 255 / 0.35) transparent;

  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: ${({ theme }) => theme.radius.pill};
    background-color: rgb(255 255 255 / 0.35);
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: rgb(255 255 255 / 0.55);
  }
`

const Item = styled.li`
  width: min(${({ theme }) => theme.player.sessionCardWidth}, 72vw);
  flex: 0 0 auto;
  scroll-snap-align: start;
`

/** Focus rings the whole card, since the title below it is part of the target. */
const CardButton = styled.button`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
  width: 100%;
  padding: 0;
  border: none;
  background: none;
  text-align: left;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }

  &:focus-visible {
    outline: 3px solid ${({ theme }) => theme.player.focusRing};
    outline-offset: 2px;
    border-radius: ${({ theme }) => theme.radius.md};
  }
`

const Artwork = styled.div<{ $isCurrent: boolean }>`
  position: relative;
  border-radius: ${({ theme }) => theme.radius.md};
  overflow: hidden;
  box-shadow: 0 0 4px 0 rgb(0 0 0 / 0.7);

  /*
   * The selected card's brand ring. Drawn as a pseudo-element inside the
   * artwork's own clipping box rather than as an outline, for two reasons: an
   * outline is painted outside the border box, where the scroll container crops
   * it on the first and last card and along the top edge; and only an inset ring
   * follows the rounded corners. It sits above the duration tag and progress bar,
   * which is why it outranks their z-index.
   */
  &::before {
    content: "";
    position: absolute;
    inset: 0;
    z-index: 2;
    border: 4px solid ${({ theme }) => theme.player.brand};
    border-radius: inherit;
    opacity: ${({ $isCurrent }) => ($isCurrent ? 1 : 0)};
    transition: opacity 120ms ease-in-out;
    pointer-events: none;
  }

  /*
   * No z-index on purpose: it must cover the artwork but stay under the duration
   * chip, the progress bar (both z-index 1) and the selection ring, which have to
   * stay legible through the dimming.
   */
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background-color: rgb(0 0 0 / 0.45);
    transition: background-color 120ms ease-in-out;
    pointer-events: none;
  }

  ${CardButton}:hover:not(:disabled) &::after,
  ${CardButton}:focus-visible &::after {
    background-color: transparent;
  }
`

/**
 * Purely an affordance — the card itself is the button, so this must not be one.
 * A nested button would be invalid markup and would give a screen reader two
 * controls where the viewer sees one.
 *
 * Above everything else in the card, including the selection ring, so it is never
 * half-hidden behind the duration tag or the progress bar.
 */
const PlayAffordance = styled.span`
  position: absolute;
  inset: 0;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.player.textPrimary};
  filter: drop-shadow(0 0 4px rgb(0 0 0 / 0.7));
  opacity: 0;
  transition: opacity 120ms ease-in-out;
  pointer-events: none;

  ${CardButton}:hover:not(:disabled) &,
  ${CardButton}:focus-visible & {
    opacity: 0.55;
  }
`

const DurationTag = styled.span`
  position: absolute;
  right: ${({ theme }) => theme.spacing.sm};
  bottom: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => `${theme.spacing.xxs} ${theme.spacing.xs}`};
  border-radius: ${({ theme }) => theme.radius.sm};
  background-color: ${({ theme }) => theme.player.track};
  color: ${({ theme }) => theme.player.textPrimary};
  font-size: ${({ theme }) => theme.fontSize.sm};
  line-height: ${({ theme }) => theme.lineHeight.tight};
  z-index: 1;
`

const ProgressTrack = styled.div`
  position: absolute;
  inset-inline: 0;
  bottom: 0;
  height: 4px;
  background-color: rgb(255 255 255 / 0.35);
  z-index: 1;
`

const ProgressFill = styled.div<{ $percent: number }>`
  width: ${({ $percent }) => `${$percent}%`};
  height: 100%;
  background-color: ${({ theme }) => theme.player.brand};
`

const Title = styled.span`
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  line-height: ${({ theme }) => theme.lineHeight.tight};
  color: ${({ theme }) => theme.player.textPrimary};
  overflow-wrap: anywhere;
`

interface PlayerSessionRailProps {
  readonly title: string
  readonly sessions: readonly DetailSession[]
  readonly currentSessionId: string
  readonly onSelectSession: (sessionId: string) => void
  readonly isOpen: boolean
  readonly qaHook: string
}

/**
 * The session browser from node 400-2757 — the same rail as the detail page, but
 * it switches the loaded video in place instead of navigating, so the player is
 * never torn down and rebuilt to move between sessions.
 *
 * A session the graph will not let us play (`playback` absent — no entitlement,
 * or no Brightcove id) still draws, disabled. Hiding it would silently renumber
 * the study for that viewer.
 */
export const PlayerSessionRail = ({
  title,
  sessions,
  currentSessionId,
  onSelectSession,
  isOpen,
  qaHook,
}: PlayerSessionRailProps): ReactElement => {
  const currentRef = useRef<HTMLLIElement | null>(null)

  // Opening the rail on session 8 of 10 should not start it at session 1.
  useEffect(() => {
    if (isOpen) {
      currentRef.current?.scrollIntoView({ block: "nearest", inline: "center" })
    }
  }, [isOpen])

  return (
    <Root aria-label={title} data-qa-hook={qaHook}>
      <Heading>{title}</Heading>

      <Rail data-qa-hook={`${qaHook}-rail`}>
        {sessions.map(session => {
          const isCurrent = session.id === currentSessionId

          return (
            <Item key={session.id} ref={isCurrent ? currentRef : undefined}>
              <CardButton
                type="button"
                disabled={!session.playback}
                aria-current={isCurrent ? "true" : undefined}
                data-qa-hook={`${qaHook}-card`}
                onClick={() => onSelectSession(session.id)}
              >
                <Artwork $isCurrent={isCurrent}>
                  <RemoteImage
                    src={session.thumbnailUrl}
                    alt=""
                    ratio={THUMBNAIL_RATIO}
                    qaHook={`${qaHook}-card-image`}
                  />
                  {/* The session already playing needs no invitation to play, and
                      one the account cannot open would be a false promise. */}
                  {session.playback && !isCurrent ? (
                    <PlayAffordance data-qa-hook={`${qaHook}-card-play`}>
                      <PlayCircleIcon size={40} />
                    </PlayAffordance>
                  ) : null}
                  {session.duration ? <DurationTag>{session.duration}</DurationTag> : null}
                  {session.progressPercent > 0 ? (
                    <ProgressTrack
                      role="progressbar"
                      aria-label={`${session.title} progress`}
                      aria-valuenow={session.progressPercent}
                      aria-valuemin={0}
                      aria-valuemax={100}
                    >
                      <ProgressFill $percent={session.progressPercent} />
                    </ProgressTrack>
                  ) : null}
                </Artwork>

                <Title>{session.title}</Title>
              </CardButton>
            </Item>
          )
        })}
      </Rail>
    </Root>
  )
}
